// src/components/molecules/SearchBar/SearchBar.jsx
import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, InputAdornment } from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';
import SearchInput from '../../atoms/SearchInput/SearchInput';

/**
 * SearchBar - Componente molecular para barra de pesquisa
 * 
 * Este componente combina o SearchInput com funcionalidades adicionais
 * como limpeza do campo e debounce. Segue o princípio do Atomic Design
 * como uma "molécula".
 */
const SearchBar = ({
  value,
  onChange,
  onSearch,
  placeholder = 'Pesquisar artigos...',
  debounceTime = 300,
  className = '',
  fullWidth,
  sx,
  inputSx,
  ...props
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [debounceTimeout, setDebounceTimeout] = useState(null);

  // Sincronizar estado interno quando value externo mudar
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    // Chama o callback onChange imediatamente (se fornecido)
    if (onChange) {
      onChange(newValue);
    }
    
    // Configura o debounce para a função de pesquisa
    if (onSearch) {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
      
      const timeout = setTimeout(() => {
        onSearch(newValue);
      }, debounceTime);
      
      setDebounceTimeout(timeout);
    }
  }, [onChange, onSearch, debounceTimeout, debounceTime]);

  const handleClear = useCallback(() => {
    setInputValue('');
    
    if (onChange) {
      onChange('');
    }
    
    if (onSearch) {
      onSearch('');
    }
  }, [onChange, onSearch]);

  return (
    <Box 
      className={`search-bar ${className}`}
      sx={{ 
        display: 'flex',
        alignItems: 'center',
        width: fullWidth ? '100%' : 260,
        minWidth: 0,
        overflow: 'hidden',
        ...sx
      }}
    >
      <SearchInput
        value={inputValue}
        onChange={handleChange}
        placeholder={placeholder}
        fullWidth
        {...props}
        InputProps={{
          endAdornment: inputValue ? (
            <InputAdornment position="end" sx={{ ml: 0.5, mr: 0.75 }}>
              <IconButton
                sx={{
                  width: 24,
                  height: 24,
                  padding: 0,
                  borderRadius: '6px',
                  color: 'text.secondary',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
                onClick={handleClear}
                size="small"
                aria-label="Limpar busca"
              >
                <ClearIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </InputAdornment>
          ) : null,
          ...(props.InputProps || {})
        }}
        sx={{
          '& .MuiInputBase-root': {
            paddingRight: '2px'
          },
          ...(inputSx || {})
        }}
      />
    </Box>
  );
};

SearchBar.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onSearch: PropTypes.func,
  placeholder: PropTypes.string,
  debounceTime: PropTypes.number,
  className: PropTypes.string,
  fullWidth: PropTypes.bool,
  sx: PropTypes.object,
  inputSx: PropTypes.object
};

export default SearchBar;
